import { useState, useMemo } from "react";
import { Eye, X, Plus, Search, Loader2, Info, Users } from "lucide-react";

// Hooks
import { useTarefaObservadores } from "@/hooks/tarefas/use-tarefa-observadores";

// UI Components
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Button, buttonVariants } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Input } from "@/components/ui/input";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Person } from "@/components/shared/person";

// Types
import { TarefaObservadorDTO } from "@/types/tarefa.type";
import { PessoaDTO } from "@/types/pessoa";

interface TarefaObservadoresCardProps {
  tarefaId: number;
  isConcluido: boolean;
  canManage: boolean;
  atribuidoAId?: number;
  criadoPorId?: number;
}

// ============================================================================
// COMPONENTES AUXILIARES
// ============================================================================

const ObserverPerson = ({
  pessoa,
  isClickable = true,
}: {
  pessoa: PessoaDTO;
  isClickable?: boolean;
}) => {
  return (
    <Person.Root pessoa={pessoa} asClickable={isClickable}>
      <Person.Avatar />
      <div className="flex flex-col">
        <Person.Name className="mb-1 text-start text-sm font-medium leading-none" format="full" />
        <Person.Email className="text-start text-[11px] leading-none text-muted-foreground" />
      </div>
    </Person.Root>
  );
};

const ObserverItem = ({
  obs,
  showRemove,
  onRemove,
}: {
  obs: TarefaObservadorDTO;
  showRemove: boolean;
  onRemove: (id: number) => void;
}) => (
  <div className="group flex items-center justify-between rounded-lg p-2 transition-all duration-200 hover:bg-accent/40">
    <div className="flex items-center gap-3 overflow-hidden">
      <ObserverPerson pessoa={obs.pessoa} />
    </div>

    {showRemove && (
      <Button
        variant="ghost"
        size="icon"
        className="h-7 w-7 opacity-0 transition-opacity hover:bg-destructive/10 group-hover:opacity-100"
        onClick={() => onRemove(obs.id)}
        title="Remover observador"
      >
        <X className="h-4 w-4 text-muted-foreground transition-colors hover:text-destructive" />
      </Button>
    )}
  </div>
);

const AddObserverMenu = ({
  open,
  onOpenChange,
  searchTerm,
  setSearchTerm,
  pessoasFiltradas,
  isAdding,
  isLoading,
  temPessoasDisponiveis,
  onSelect,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  searchTerm: string;
  setSearchTerm: (term: string) => void;
  pessoasFiltradas: PessoaDTO[];
  isAdding: boolean;
  isLoading: boolean;
  temPessoasDisponiveis: boolean;
  onSelect: (id: number) => void;
}) => (
  <div className="border-t border-border/40 bg-muted/10 p-3">
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger
        className={buttonVariants({
          variant: "outline",
          className:
            "h-9 w-full justify-between bg-background text-xs shadow-sm transition-colors hover:bg-accent/50",
        })}
        disabled={isAdding || isLoading || !temPessoasDisponiveis}
      >
        {isAdding ? (
          <span className="flex items-center gap-2 text-muted-foreground">
            <Loader2 className="h-3.5 w-3.5 animate-spin" /> Vinculando...
          </span>
        ) : !temPessoasDisponiveis ? (
          <span className="text-muted-foreground">Ninguém disponível</span>
        ) : (
          <span className="flex items-center gap-2 font-medium text-foreground">
            <Plus className="h-3.5 w-3.5" /> Adicionar observador
          </span>
        )}
      </PopoverTrigger>

      <PopoverContent
        className="pointer-events-auto w-[320px] p-2 shadow-lg"
        align="center"
        sideOffset={8}
      >
        <div className="relative mb-3">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Pesquisar por nome..."
            className="h-9 bg-muted/30 pl-9 text-sm focus-visible:ring-1 focus-visible:ring-offset-0"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            autoFocus
          />
        </div>

        <ScrollArea className="custom-scrollbar h-[220px] pr-2">
          {pessoasFiltradas.length === 0 ? (
            <div className="flex h-full flex-col items-center justify-center space-y-2 py-8 text-muted-foreground">
              <Search className="h-8 w-8 opacity-20" />
              <p className="text-xs">Nenhum resultado encontrado.</p>
            </div>
          ) : (
            <div className="flex flex-col gap-1">
              {pessoasFiltradas.map((pessoa) => (
                <Button
                  key={pessoa.id}
                  variant="ghost"
                  className="z-10 h-auto justify-start rounded-lg px-2 py-2 font-normal hover:bg-accent hover:text-accent-foreground"
                  onClick={() => onSelect(pessoa.id)}
                >
                  <ObserverPerson pessoa={pessoa} isClickable={false} />
                </Button>
              ))}
            </div>
          )}
        </ScrollArea>
      </PopoverContent>
    </Popover>
  </div>
);

// ============================================================================
// COMPONENTE PRINCIPAL
// ============================================================================

export const TarefaObservadoresCard = ({
  tarefaId,
  isConcluido,
  canManage,
  atribuidoAId,
  criadoPorId,
}: TarefaObservadoresCardProps) => {
  const [open, setOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  const { observadores, pessoasDisponiveis, isLoading, addObservador, isAdding, removeObservador } =
    useTarefaObservadores(tarefaId, atribuidoAId, criadoPorId);

  const pessoasFiltradas = useMemo(() => {
    if (!searchTerm) return pessoasDisponiveis;
    const lowerSearch = searchTerm.toLowerCase();
    return pessoasDisponiveis?.filter((p) => p.nome.toLowerCase().includes(lowerSearch));
  }, [pessoasDisponiveis, searchTerm]);

  const showAddSection = canManage && !isConcluido;

  const handleSelectPessoa = async (pessoaId: number) => {
    setOpen(false);
    setSearchTerm("");
    await addObservador([pessoaId]);
  };

  const handleOpenChange = (isOpen: boolean) => {
    setOpen(isOpen);
    if (!isOpen) setSearchTerm("");
  };

  return (
    <Card className="flex max-h-[450px] flex-col overflow-hidden border-border/60 shadow-sm">
      <CardHeader className="border-b border-border/40 bg-muted/20 px-4 pb-3">
        <CardTitle className="flex items-center gap-2 text-sm font-semibold text-foreground">
          <Eye className="h-4 w-4 text-primary dark:text-emerald-500" />
          Observadores
          {/* O Tooltip Premium */}
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger className="rounded-full ring-primary ring-offset-background focus:outline-none focus-visible:ring-2">
                <Info className="h-4 w-4 cursor-help text-muted-foreground/70 transition-colors hover:text-foreground" />
              </TooltipTrigger>
              <TooltipContent side="top" className="max-w-[280px] p-4 shadow-xl">
                <div className="space-y-2">
                  <p className="flex items-center gap-2 text-sm font-medium">
                    <Users className="h-4 w-4 text-primary" /> Papel do Observador
                  </p>
                  <p className="text-xs leading-relaxed text-muted-foreground">
                    Acompanham o andamento, recebem notificações, participam do chat e anexam
                    arquivos.
                    <strong className="mt-1 block text-foreground">
                      Eles não podem alterar o status da tarefa.
                    </strong>
                  </p>
                </div>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
          {!isLoading && observadores.length > 0 && (
            <span className="ml-auto flex min-h-5 min-w-5 items-center justify-center rounded-full bg-primary/10 px-2 py-0.5 text-[10px] font-bold text-primary">
              {observadores.length}
            </span>
          )}
        </CardTitle>
      </CardHeader>

      <CardContent className="flex min-h-0 flex-1 flex-col bg-background p-0">
        <ScrollArea className="custom-scrollbar flex-1 px-2 py-3">
          {isLoading && (
            <div className="flex h-full flex-col items-center justify-center space-y-3 py-6">
              <Loader2 className="h-5 w-5 animate-spin text-primary/50" />
              <p className="text-xs text-muted-foreground">Carregando...</p>
            </div>
          )}

          {/* O Empty State Refinado */}
          {!isLoading && observadores.length === 0 && (
            <div className="flex flex-col items-center justify-center space-y-3 px-4 py-8 text-center">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-muted/50">
                <Eye className="h-5 w-5 text-muted-foreground/50" />
              </div>
              <p className="text-sm text-muted-foreground">Nenhum observador definido.</p>
            </div>
          )}

          {!isLoading && observadores.length > 0 && (
            <div className="space-y-1.5">
              {observadores.map((obs) => (
                <ObserverItem
                  key={obs.id}
                  obs={obs}
                  showRemove={showAddSection}
                  onRemove={removeObservador}
                />
              ))}
            </div>
          )}
        </ScrollArea>

        {showAddSection && (
          <AddObserverMenu
            open={open}
            onOpenChange={handleOpenChange}
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            pessoasFiltradas={pessoasFiltradas}
            isAdding={isAdding}
            isLoading={isLoading}
            temPessoasDisponiveis={pessoasDisponiveis.length > 0}
            onSelect={handleSelectPessoa}
          />
        )}
      </CardContent>
    </Card>
  );
};
